import { difference } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Collapse, Label } from 'reactstrap';
import { Campaign, CustomerDataColumn, Me, Person } from 'types';
import {
  getCustomerField,
  getCustomerKey,
  isCustomerField,
} from 'utils/util/customerData';
import { convertDateTimeToLocalString } from 'utils/util/date';
import {
  AdvancedCurrentPeopleFilterRoleUserApiComponent,
  AdvancedDatasetFilterWithIndividualsCalibrationViewApiComponent,
  AdvancedDatasetFilterWithIndividualsCalibrationViewEditorApiComponent,
  AdvancedDatasetFilterWithIndividualsHrbpApiComponent,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import { TGroup } from 'views/Widgets/Filters/types';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { defaultUnselectedFields } from 'views/Widgets/People/FilterablePeopleTable/FilterablePeopleTableColumnSelectorModal/hooks';
import SortableColumnSelector from 'views/Widgets/People/FilterablePeopleTable/FilterablePeopleTableColumnSelectorModal/SortableColumnSelector';
import { ColumnDescriptor } from 'views/Widgets/People/FilterablePeopleTable/FilterablePeopleTableColumnSelectorModal/types';
import { CALIBRATION_STATUS_META } from '../constants';
import type {
  CalibrationViewStatus,
  CalibrationViewSummary,
  CreateViewFormState,
} from '../types';
import { isOwner, isOwnerOrAdmin } from '../utils/calibration';
import CustomerColumnsAvailabilityTable from './CustomerColumnsAvailabilityTable';
import ReviewersFieldWithFilter from './ReviewersFieldWithFilter';

type ModalMode = 'create' | 'edit';

const EMPTY_LIST = [];
const EMPTY_OBJECT = {};

interface ModalEditorFormData {
  name: string;
  description: string;
  dueDate: string;
  status: CalibrationViewStatus;
  ownerPerson: Person[];
  administrators: Person[];
  reviewers: Person[];
  reviewees_filter: TGroup | null;
  visible_fields?: string[];
  add_to_all_views?: boolean;
  remove_from_all_views?: boolean;
}

export type CalibrationViewModalProps = {
  me?: Me | null;
  currentProxyPerson?: Person | null;
  isOpen: boolean;
  mode: ModalMode;
  adminFilter?: ApiGroup;
  campaign?: Campaign;
  organizationId?: number;
  editingView?: CalibrationViewSummary | null;
  initialValues?: Partial<CreateViewFormState>;
  columns?: ColumnDescriptor[];
  visibleColumns?: string[];
  onSuccess: (views: CalibrationViewSummary[]) => void;
  onClose: () => void;
  isAdmin?: boolean;
  ownedCustomerColumns?: CustomerDataColumn[];
  // Whether the current person is an HRBP of the organization. HRBPs pick
  // reviewees through the HRBP-scoped dataset filters; calibration view
  // editors through the ones scoped to their role's target audience
  isHRBP: boolean;
};

const CalibrationViewModal: React.FC<CalibrationViewModalProps> = ({
  me,
  currentProxyPerson,
  isOpen,
  mode,
  adminFilter,
  campaign,
  organizationId,
  editingView,
  initialValues,
  columns,
  visibleColumns,
  onSuccess,
  onClose,
  isAdmin,
  ownedCustomerColumns,
  isHRBP,
}) => {
  const { formatMessage } = useIntl();
  const isEditMode = mode === 'edit';

  const [addedColumns, setAddedColumns] = useState<string[]>([]);
  const [removedColumns, setRemovedColumns] = useState<string[]>([]);
  const [customerDataColumns, setCustomerDataColumns] = useState<
    Record<string, { available: boolean }>
  >(() => {
    const result: Record<string, { available: boolean }> = {};
    const stored = editingView?.config?.customer_data_columns;
    if (stored && typeof stored === 'object') {
      for (const [key, entry] of Object.entries(stored)) {
        if (entry?.available) {
          result[key] = entry;
        }
      }
    }
    return result;
  });
  const [showAdminFilter, setShowAdminFilter] = useState(false);

  const ownedCustomerKeys = useMemo(
    () => new Set((ownedCustomerColumns ?? []).map((col) => col.key)),
    [ownedCustomerColumns]
  );

  // Whether a field belongs in the selector. The backend already scopes `columns` to
  // the customer-data columns available to this view, so columns owned by others are
  // always shown. The current user's own columns are additionally gated by the
  // in-session availability toggle, so making one unavailable drops it from the list
  // (and from the saved visible columns) immediately.
  const isAvailableField = useCallback(
    (field: string) => {
      if (!isCustomerField(field)) return true;
      const key = getCustomerKey(field);
      if (ownedCustomerKeys.has(key)) {
        return !!customerDataColumns[key]?.available;
      }
      return true;
    },
    [customerDataColumns, ownedCustomerKeys]
  );

  // Standard and customer-data columns share a single visible-columns selector. The
  // backend scopes `columns` to the customer-data columns available to this view, and
  // we additionally honor in-session availability toggles so unavailable columns drop
  // out of the list immediately. Owned columns made available in-session aren't in
  // `columns` yet (the backend only includes already-available columns), so we
  // synthesize descriptors for them so they surface in the selector immediately.
  const selectableColumns = useMemo(() => {
    const base = (columns ?? []).filter((col) => isAvailableField(col.field));
    const existingFields = new Set(base.map((col) => col.field));
    const newlyAvailable: ColumnDescriptor[] = (ownedCustomerColumns ?? [])
      .filter(
        (col) =>
          customerDataColumns[col.key]?.available &&
          !existingFields.has(getCustomerField(col.key))
      )
      .map((col) => ({
        field: getCustomerField(col.key),
        name: col.label,
      }));
    return [...base, ...newlyAvailable];
  }, [columns, isAvailableField, ownedCustomerColumns, customerDataColumns]);

  const selectableVisibleColumns = useMemo(
    () => (visibleColumns ?? []).filter((colName) => isAvailableField(colName)),
    [visibleColumns, isAvailableField]
  );

  // Columns that duplicate another column's id are left unticked by default,
  // matching the shared people-table selector.
  const initialVisibleFields = useMemo(() => {
    const defaultUnselected = new Set(
      defaultUnselectedFields(selectableColumns)
    );
    return {
      visible_fields: selectableVisibleColumns.filter(
        (field) => !defaultUnselected.has(field)
      ),
    };
  }, [selectableColumns, selectableVisibleColumns]);

  const isViewOwner = useMemo(() => {
    const personToCheck = currentProxyPerson || me;
    return isOwner(personToCheck, editingView);
  }, [editingView, currentProxyPerson, me]);

  const canEditReviewees = useMemo(
    () => !isEditMode || isOwnerOrAdmin(currentProxyPerson ?? me, editingView),
    [currentProxyPerson, editingView, isEditMode, me]
  );

  // Which dataset filters back the reviewees picker. Creating a view, or
  // editing one as its owner, uses the current person's own scope: the HRBP
  // one for HRBPs, otherwise (a calibration view editor) the target audience
  // of their role. Once a view exists it stores that audience, so non-HRBP
  // owners and other administrators use the view scope
  const revieweesFilterComponent = useMemo(() => {
    if (!isEditMode) {
      return isHRBP
        ? AdvancedDatasetFilterWithIndividualsHrbpApiComponent
        : AdvancedDatasetFilterWithIndividualsCalibrationViewEditorApiComponent;
    }
    if (isViewOwner && isHRBP) {
      return AdvancedDatasetFilterWithIndividualsHrbpApiComponent;
    }
    return AdvancedDatasetFilterWithIndividualsCalibrationViewApiComponent;
  }, [isEditMode, isHRBP, isViewOwner]);

  // Prepare initial object for ModalEditor
  const initialObject = useMemo(() => {
    const checkboxes = {
      add_to_all_views: false,
      remove_from_all_views: false,
    };
    if (isEditMode && editingView) {
      return {
        id: editingView.id,
        name: editingView.name || '',
        description: editingView.description || '',
        dueDate: editingView.due_date || '',
        status: editingView.status || 'D',
        ownerPerson: editingView.ownerPerson ? [editingView.ownerPerson] : [me],
        administrators: editingView.administrators || [],
        reviewers: editingView.reviewers || [],
        reviewees_filter: editingView?.config?.reviewees_filter ?? null,
        admin_filter: adminFilter,
        ...(columns ? initialVisibleFields : EMPTY_OBJECT),
        ...checkboxes,
      };
    }
    return {
      name: initialValues?.name || '',
      description: initialValues?.description || '',
      dueDate: initialValues?.due_date || '',
      status: initialValues?.status || 'D',
      ownerPerson: initialValues?.ownerPerson
        ? [initialValues.ownerPerson]
        : [me],
      administrators: initialValues?.administrators || [],
      reviewers: initialValues?.reviewers || [],
      reviewees_filter: editingView?.config?.reviewees_filter ?? null,
      admin_filter: adminFilter,
      ...(columns ? initialVisibleFields : EMPTY_OBJECT),
      ...checkboxes,
    };
  }, [
    isEditMode,
    editingView,
    initialValues,
    me,
    adminFilter,
    columns,
    initialVisibleFields,
  ]);

  // Transform the form data to the API payload format
  const transformObjectBeforeSubmit = useCallback(
    (data: ModalEditorFormData) => {
      // Transform ownerPerson from array to single person
      const ownerPerson =
        Array.isArray(data.ownerPerson) && data.ownerPerson.length > 0
          ? data.ownerPerson[0]
          : null;

      const dueDate = new Date(data.dueDate);
      const dueDateString = convertDateTimeToLocalString(dueDate);

      return {
        organization_id: organizationId,
        campaign_id: campaign?.id,
        name: data.name,
        description: data.description || '',
        status: data.status,
        due_date: dueDateString,
        owner_person_id: ownerPerson!.id,
        administrator_ids: (data.administrators || []).map((p: Person) => p.id),
        reviewer_ids: (data.reviewers || []).map((p: Person) => p.id),
        config: {
          reviewees_filter: data.reviewees_filter || null,
          // Drop any customer-data columns that are no longer available so unavailable
          // columns are persisted as not visible.
          visible_columns: (data.visible_fields ?? []).filter((field) =>
            isAvailableField(field)
          ),
          customer_data_columns: customerDataColumns,
        },
        options: {
          removed_columns: removedColumns,
          added_columns: addedColumns,
          add_to_all_views: data.add_to_all_views,
          remove_from_all_views: data.remove_from_all_views,
        },
      };
    },
    [
      campaign?.id,
      organizationId,
      addedColumns,
      removedColumns,
      customerDataColumns,
      isAvailableField,
    ]
  );

  const statusOptions = useMemo(
    () =>
      Object.entries(CALIBRATION_STATUS_META(formatMessage)).map(
        ([key, meta]) => ({
          id: key,
          name: meta.name,
          icon: meta.Icon,
          iconClassName: meta.iconClassName,
        })
      ),
    [formatMessage]
  );

  const renderStatusItem = useCallback((option) => {
    const { icon: Icon, iconClassName, name } = option;
    return (
      <span className="d-inline-flex align-items-center gap-2">
        <Icon size={16} className={iconClassName} />
        {name}
      </span>
    );
  }, []);

  const handleCustomerAvailabilityChange = useCallback(
    (next: Record<string, { available: boolean }>) => {
      setCustomerDataColumns(next);
    },
    []
  );

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.TEXT,
        name: 'name',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.name',
          defaultMessage: 'View name',
        }),
        placeholder: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.name.placeholder',
          defaultMessage: 'e.g. Marketing leads calibration',
        }),
        required: true,
        autoFocus: true,
      },
      {
        type: INPUT_TYPES.DATE_PICKER,
        name: 'dueDate',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.due_date',
          defaultMessage: 'Due date',
        }),
        helperText: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.due_date.help',
          defaultMessage:
            'Share when facilitators should wrap up decisions or publish outcomes.',
        }),
        required: true,
      },
      {
        type: INPUT_TYPES.TEXTAREA,
        name: 'description',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.description',
          defaultMessage: 'Purpose and scope',
        }),
        placeholder: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.description.placeholder',
          defaultMessage:
            'Outline the roles, levels, or focus topics included in this session.',
        }),
        rows: 3,
      },
      {
        type: INPUT_TYPES.DROPDOWN,
        name: 'status',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.status',
          defaultMessage: 'Status',
        }),
        objects: statusOptions,
        defaultValue: statusOptions[0],
        renderItem: renderStatusItem,
        required: true,
        buttonClassName: 'd-flex align-items-center',
      },
      {
        type: INPUT_TYPES.PEOPLE_EDITOR,
        name: 'ownerPerson',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.detail.owner',
          defaultMessage: 'Owner',
        }),
        placeholder: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.owner.placeholder',
          defaultMessage: 'Search for a view owner',
        }),
        maxSelections: 1,
        minSelections: 1,
        required: true,
        allowSelf: true,
        disabled: true,
      },
      {
        type: INPUT_TYPES.PEOPLE_EDITOR,
        name: 'administrators',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.detail.administrators',
          defaultMessage: 'Administrators',
        }),
        placeholder: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.administrators.placeholder',
          defaultMessage: 'Add facilitators or HR partners',
        }),
        helperText: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.administrators.helper_text',
          defaultMessage:
            'Add HR partners who you want to edit columns, calibrate ratings, and view admin-only private comments.',
        }),
        allowSelf: true,
      },
      {
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        name: 'reviewers',
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.detail.reviewers',
          defaultMessage: 'Reviewers',
        }),
        placeholder: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.reviewers.placeholder',
          defaultMessage: 'Add reviewers',
        }),
        helperText: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.reviewers.helper_text',
          defaultMessage:
            'Add participants in the calibration, typically people managers who you want to contribute to the calibration conversation and prework.',
        }),
        required: true,
        minSelections: 1,
        allowSelf: true,
        component: ReviewersFieldWithFilter,
      },
      ...(canEditReviewees
        ? [
            {
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              name: 'reviewees_filter',
              label: (
                <>
                  <Label>
                    <FormattedMessage
                      id="app.views.widgets.dashboards.calibration_dashboard.detail.reviewees"
                      defaultMessage="Reviewees filter"
                    />
                  </Label>

                  <div className="mb-1 form-text text-muted">
                    {isViewOwner || !isEditMode ? (
                      <FormattedMessage
                        id="app.views.widgets.dashboards.calibration_dashboard.detail.admin_filter_owner"
                        defaultMessage="Filter the group of people whose ratings will be adjusted during calibration. You can only add people within your HR administration scope in Confirm."
                      />
                    ) : (
                      <FormattedMessage
                        id="app.views.widgets.dashboards.calibration_dashboard.detail.admin_filter"
                        defaultMessage="Filter the group of people whose ratings will be adjusted during calibration. You can only add people within the view owner's HR administration scope in Confirm."
                      />
                    )}
                    {adminFilter ? (
                      <>
                        <span
                          role="button"
                          className="btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1"
                          onClick={() => setShowAdminFilter((prev) => !prev)}
                        >
                          <i
                            className={`fe fe-chevron-${
                              showAdminFilter ? 'up' : 'down'
                            }`}
                          />
                          <FormattedMessage
                            id="app.views.widgets.dashboards.calibration_dashboard.detail.admin_filter_scope"
                            defaultMessage="View administration scope"
                          />
                        </span>
                        <Collapse isOpen={showAdminFilter}>
                          <AdvancedCurrentPeopleFilterRoleUserApiComponent
                            disabled={true}
                            value={adminFilter}
                          />
                        </Collapse>
                      </>
                    ) : null}
                  </div>
                </>
              ),
              campaign: campaign,
              calibrationViewId: editingView?.id,
              component: revieweesFilterComponent,
            },
          ]
        : []),
      ...(columns && columns.length
        ? [
            {
              type: INPUT_TYPES.CUSTOM_INPUT,
              name: '_selected_columns_label',
              formGroupClassName: 'mb-0',
              component: (
                <Label>
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.components.calibration_view_modal.visible_columns"
                    defaultMessage="Visible columns"
                  />
                </Label>
              ),
            },
            {
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              name: 'visible_fields',
              component: SortableColumnSelector,
              columnDescriptors: selectableColumns.length
                ? selectableColumns
                : EMPTY_LIST,
            },
            ...(isAdmin && addedColumns.length
              ? [
                  {
                    type: INPUT_TYPES.CHECKBOX,
                    name: 'add_to_all_views',
                    label: formatMessage({
                      id: 'app.views.widgets.dashboards.calibration_dashboard.detail.add_to_all_views',
                      defaultMessage:
                        'Add added columns to all calibration views for this campaign',
                    }),
                  },
                ]
              : []),
            ...(isAdmin && removedColumns.length
              ? [
                  {
                    type: INPUT_TYPES.CHECKBOX,
                    name: 'remove_from_all_views',
                    label: formatMessage({
                      id: 'app.views.widgets.dashboards.calibration_dashboard.detail.remove_from_all_views',
                      defaultMessage:
                        'Remove removed columns to all calibration views for this campaign',
                    }),
                  },
                ]
              : []),
            ...(ownedCustomerColumns && ownedCustomerColumns.length
              ? [
                  {
                    type: INPUT_TYPES.CUSTOM_INPUT,
                    name: '_owned_customer_columns_label',
                    formGroupClassName: 'mb-0 mt-3',
                    component: (
                      <Label>
                        <FormattedMessage
                          id="app.views.widgets.dashboards.calibration_dashboard.components.calibration_view_modal.owned_customer_columns"
                          defaultMessage="Your custom data columns"
                        />
                      </Label>
                    ),
                  },
                  {
                    type: INPUT_TYPES.CUSTOM_INPUT,
                    name: '_owned_customer_columns_table',
                    formGroupClassName: 'mb-0',
                    component: (
                      <CustomerColumnsAvailabilityTable
                        ownedColumns={ownedCustomerColumns}
                        value={customerDataColumns}
                        onChange={handleCustomerAvailabilityChange}
                      />
                    ),
                  },
                ]
              : []),
          ]
        : []),
    ],
    [
      formatMessage,
      statusOptions,
      renderStatusItem,
      campaign,
      adminFilter,
      columns,
      selectableColumns,
      isAdmin,
      addedColumns,
      removedColumns,
      isViewOwner,
      editingView?.id,
      isEditMode,
      mode,
      canEditReviewees,
      revieweesFilterComponent,
      ownedCustomerColumns,
      customerDataColumns,
      handleCustomerAvailabilityChange,
      showAdminFilter,
    ]
  );

  const handleOnChange = useCallback(
    (data: ModalEditorFormData) => {
      if (columns?.length) {
        // "Apply to all views" only applies to standard columns; customer-data column
        // visibility is per-view, so exclude customer fields from the add/remove diff.
        const standardSelected = (data.visible_fields ?? []).filter(
          (field) => !isCustomerField(field)
        );
        const standardVisible = (visibleColumns ?? []).filter(
          (field) => !isCustomerField(field)
        );
        const added = difference(standardSelected, standardVisible);
        const removed = difference(standardVisible, standardSelected);
        setAddedColumns(added);
        setRemovedColumns(removed);
      }
    },
    [columns, visibleColumns]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={onClose}
      callback={onSuccess}
      triggerCallbackBeforeClose={true}
      url={'/calibration-views' + (isEditMode ? `/${editingView?.id}` : '')}
      method={isEditMode ? 'PATCH' : 'POST'}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      object={initialObject}
      title={
        isEditMode
          ? formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.edit_view_modal.title',
              defaultMessage: 'Edit calibration view',
            })
          : formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.title',
              defaultMessage: 'Create calibration view',
            })
      }
      submitText={
        isEditMode
          ? formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.edit_view_modal.submit',
              defaultMessage: 'Save changes',
            })
          : formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.create_view_modal.submit',
              defaultMessage: 'Create view',
            })
      }
      inputs={inputs}
      onChange={handleOnChange}
      size="lg"
    />
  );
};

export default CalibrationViewModal;
